import { useState } from "react";
import { format } from "date-fns";
import { enUS, it as itLocale } from "date-fns/locale";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const datePickerDayClasses = {
  day_selected: "bg-navy text-gold hover:bg-navy hover:text-gold focus:bg-navy focus:text-gold",
  day_today: "text-gold-dark font-bold",
  vhidden: "sr-only",
  caption_label: "sr-only",
  caption_dropdowns: "flex items-center gap-2",
  dropdown: "border border-navy/15 bg-white px-1 py-0.5 text-xs text-navy outline-none",
  nav: "hidden",
};

export const DatePickerField = ({ value, onChange, testid, placeholder = "—", fromYear = 1930, toYear, lang = "it" }) => {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="flex items-center gap-2 border border-navy/15 bg-white px-3 py-1.5 text-xs text-navy outline-none transition-colors hover:border-gold"
        >
          <CalendarIcon size={12} className="text-gold-dark" />
          {date ? format(date, "dd/MM/yyyy") : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto border-gold/30 bg-white p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          defaultMonth={date || new Date(1990, 0, 1)}
          onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
          captionLayout="dropdown-buttons"
          fromYear={fromYear}
          toYear={toYear ?? new Date().getFullYear()}
          locale={lang === "it" ? itLocale : enUS}
          classNames={datePickerDayClasses}
        />
      </PopoverContent>
    </Popover>
  );
};

export const MonthPickerField = ({ value, onChange, testid, placeholder = "—", lang = "it" }) => {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => (value ? Number(value.slice(0, 4)) : new Date().getFullYear()));
  const monthLocale = lang === "it" ? itLocale : enUS;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="flex items-center gap-2 border border-navy/15 bg-white px-3 py-2 text-xs text-navy outline-none transition-colors hover:border-gold"
        >
          <CalendarIcon size={12} className="text-gold-dark" />
          {value || placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 border-gold/30 bg-white p-3" align="end">
        <div className="mb-2 flex items-center justify-between">
          <button type="button" onClick={() => setYear((y) => y - 1)} className="p-1 text-navy/50 transition-colors hover:text-gold-dark" aria-label="previous year">
            <ChevronLeft size={14} />
          </button>
          <span className="font-display text-sm text-navy">{year}</span>
          <button type="button" onClick={() => setYear((y) => y + 1)} className="p-1 text-navy/50 transition-colors hover:text-gold-dark" aria-label="next year">
            <ChevronRight size={14} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => {
            const v = `${year}-${String(i + 1).padStart(2, "0")}`;
            return (
              <button
                key={v}
                type="button"
                data-testid={`${testid}-month-${v}`}
                onClick={() => { onChange(v); setOpen(false); }}
                className={`px-2 py-1.5 text-[11px] uppercase tracking-[0.08em] transition-colors ${
                  value === v ? "bg-navy text-gold" : "text-navy/60 hover:bg-surface hover:text-gold-dark"
                }`}
              >
                {format(new Date(year, i, 1), "MMM", { locale: monthLocale })}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
